import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDashboardOverview } from "../../api/services/dashboardApi";
import { getErrorMessage } from "../../api/errorMessage";
import { pushToast } from "./toastSlice";
import {
  queueServiceRequestAnalysis,
  removeServiceRequest,
  sendServiceRequestReply,
  updateServiceRequest,
} from "./serviceRequestsSlice";
import {
  removeContactMessage,
  sendContactMessageReply,
  updateContactMessage,
} from "./contactMessagesSlice";
import {
  aiBucketFromAnalysis,
  bumpActivityReplyCount,
  bumpOverviewRepliesTotal,
  mergeContactMessageUpdateIntoOverview,
  mergeContactProcessingIntoOverviewActivity,
  mergeRequestAiIntoOverviewActivity,
  mergeServiceRequestUpdateIntoOverview,
  removeContactMessageFromOverview,
  removeServiceRequestFromOverview,
  shiftOverviewAiBucket,
} from "../../utils/dashboardOverviewSync";

export const fetchDashboardOverview = createAsyncThunk(
  "dashboard/fetchOverview",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getDashboardOverview();
      return data;
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Dashboard could not load",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const initialState = {
  data: null,
  status: "idle",
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboard(state) {
      state.data = null;
      state.status = "idle";
      state.error = null;
    },
    patchOverviewAfterRequestAnalysisSocket(state, action) {
      const { id, fromBucket, toBucket, aiAnalysis } = action.payload || {};
      if (!state.data || id == null) return;
      shiftOverviewAiBucket(state.data, fromBucket, toBucket);
      mergeRequestAiIntoOverviewActivity(state.data, String(id), aiAnalysis);
    },
    patchOverviewAfterContactProcessingSocket(state, action) {
      const { id, processing } = action.payload || {};
      if (!state.data || id == null) return;
      mergeContactProcessingIntoOverviewActivity(state.data, String(id), processing);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(removeServiceRequest.fulfilled, (state, action) => {
        const { id, row } = action.payload;
        if (state.data) removeServiceRequestFromOverview(state.data, id, row);
      })
      .addCase(removeContactMessage.fulfilled, (state, action) => {
        const { id, row } = action.payload;
        if (state.data) removeContactMessageFromOverview(state.data, id, row);
      })
      .addCase(updateServiceRequest.fulfilled, (state, action) => {
        const { id, data, prev } = action.payload;
        if (state.data && prev) mergeServiceRequestUpdateIntoOverview(state.data, id, prev, data);
      })
      .addCase(updateContactMessage.fulfilled, (state, action) => {
        const { id, data, prev } = action.payload;
        if (state.data && prev) mergeContactMessageUpdateIntoOverview(state.data, id, prev, data);
      })
      .addCase(queueServiceRequestAnalysis.fulfilled, (state, action) => {
        const { prev } = action.payload;
        if (!state.data || !prev) return;
        const fromBucket = aiBucketFromAnalysis(prev.aiAnalysis);
        shiftOverviewAiBucket(state.data, fromBucket, "pending");
      })
      .addCase(sendServiceRequestReply.fulfilled, (state, action) => {
        if (!state.data) return;
        bumpOverviewRepliesTotal(state.data);
        bumpActivityReplyCount(state.data, action.payload.id, "request");
      })
      .addCase(sendContactMessageReply.fulfilled, (state, action) => {
        if (!state.data) return;
        bumpOverviewRepliesTotal(state.data);
        bumpActivityReplyCount(state.data, action.payload.id, "contact");
      });
  },
});

export const {
  clearDashboard,
  patchOverviewAfterRequestAnalysisSocket,
  patchOverviewAfterContactProcessingSocket,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
